import type { GeneratorModelSlug } from '@/consts/generatorModelSlugs';
import type {
  CatalogRow,
  CatalogTool,
  ToolCatalogCard,
  ToolkitCatalog,
  ToolkitCatalogSource,
} from '@/types/toolkit.types';

/** The system tool ids the Toolkit's tool cards link. */
export function linkedToolIds(source: ToolkitCatalogSource): string[] {
  return source.rows.flatMap((row) => (row.kind === 'tools' ? row.cards.map((card) => card.toolId) : []));
}

/**
 * Fills each tool card with its system tool's words and picture. A card whose tool did not come back is
 * left out, and so is a row left with no cards. With `showMissing`, for internal users, such a card stays,
 * opens nothing and is marked, so a missing tool is seen and not hidden. A model card opens the generator,
 * so it is kept only when `isModelOffered` says the generator offers that model to this person.
 */
export function resolveToolkitCatalog(
  source: ToolkitCatalogSource,
  tools: CatalogTool[],
  showMissing: boolean,
  isModelOffered: (modelId: GeneratorModelSlug) => boolean,
): ToolkitCatalog {
  const toolsById = new Map(tools.map((tool) => [tool.toolId, tool]));
  const rows = source.rows.map((row): CatalogRow => {
    if (row.kind === 'models') {
      return { ...row, cards: row.cards.filter((card) => isModelOffered(card.id)) };
    }
    const cards = row.cards.flatMap((card): ToolCatalogCard[] => {
      const tool = toolsById.get(card.toolId);
      if (!tool) {
        // The card's words live only in the backoffice, so its id is the only name there is to show.
        return showMissing ? [{ id: card.id, name: card.id, description: '', isToolMissing: true }] : [];
      }
      return [
        {
          id: card.id,
          toolId: card.toolId,
          name: tool.name,
          description: tool.description ?? '',
          thumbnail: tool.thumbnail ?? undefined,
        },
      ];
    });
    return { ...row, cards };
  });
  return { rows: rows.filter((row) => row.cards.length > 0) };
}
